<template>
  <Modal
    actions
    name="RESCHEDULE_APPOINTMENT_MODAL"
    :title="$t('appointment.reschedule.title')"
    ref="rescheduleAppointmentModal"
  >
    <template v-slot:body>
      <v-progress-linear
        v-if="isAppointmentLoading"
        class="mt-5 mb-7"
        indeterminate
        rounded
      />

      <div v-show="!isAppointmentLoading">
        <AppointmentFormDetails :appointment="appointment" />

        <v-alert v-if="dateOrHourChanged && isUrgent" class="error lighten-4">
          <span class="d-block error--text text--darken-2">
            {{ $t('appointment.formCreate.summary.urgencyDescription') }}
          </span>
        </v-alert>

        <v-form ref="appointmentFormReschedule" lazy-validation>
          <v-btn-toggle
            v-model="isByHours"
            mandatory
            class="reschedule-type-toggle mb-4"
          >
            <v-btn :value="true" class="reschedule-type-toggle__item">
              <v-icon v-if="isByHours" color="white" left small>mdi-check</v-icon>
              <span :class="isByHours ? 'white--text' : ''">
                {{ $t('appointment.reschedule.byHour') }}
              </span>
            </v-btn>
            <v-btn :value="false" class="reschedule-type-toggle__item">
              <v-icon v-if="!isByHours" color="white" left small>mdi-check</v-icon>
              <span :class="!isByHours ? 'white--text' : ''">
                {{ $t('appointment.reschedule.byPeriod') }}
              </span>
            </v-btn>
          </v-btn-toggle>

          <v-row class="mb-3">
            <v-col cols="6" md="4">
              <SelectDate
                v-model="date"
                :data="appointment.date"
                :rules="[rules.required]"
                :label="$t('appointment.reschedule.date')"
                :message="disabled ? $t('appointment.reschedule.pastDate') : ''"
                :error="disabled"
                @input="dateOrHourChanged = true"
                ref="appointmentDate"
              />
            </v-col>
            <v-col v-if="isByHours" cols="6" md="3">
              <SelectHour
                v-model="hour"
                :date="date"
                :data="dateHour"
                :rules="[rules.required]"
                :label="$t('appointment.reschedule.hour')"
                :startDateDelay="startDateDelay"
                limitHours
                @input="dateOrHourChanged = true"
              />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field
                v-if="!isAppointmentLoading"
                v-model.lazy="displacement"
                v-money="catalogCurrency()"
                :label="$t('appointment.reschedule.displacement')"
                :error-messages="
                  errorDisplacementComparison
                    ? $t('appointment.reschedule.cantBeLess', { value: currentDisplacement.toFixed(2) })
                    : ''
                "
                :error="errorDisplacementComparison"
                class="amount amount--small inputAmount"
                hide-details="auto"
                outlined
              />
            </v-col>
          </v-row>

          <HPeriodPicker
            v-if="!isByHours"
            :selectedDate="date"
            :label="$t('appointment.reschedule.selectPeriod')"
            :show-custom-time="false"
            class="mb-4"
            @input="hour = $event; dateOrHourChanged = true"
            @setSlotSize="slot_size = $event || 0; isOutdatedAppointment(date, hour)"
          />

          <HSlotSize v-if="isByHours && country === 'AU'" v-model="slot_size" :default-value="slot_size" />

          <v-textarea
            :label="$t('appointment.reschedule.observation')"
            v-model="observations"
            hide-details
            outlined
            rows="3"
          />

          <v-textarea
            class="mt-4"
            :label="$t('appointment.reschedule.rescheduleReason')"
            :placeholder="$t('appointment.reschedule.rescheduleReasonPlaceholder')"
            v-model="userRescheduleReason"
            :maxlength="500"
            hide-details
            outlined
            rows="3"
          />
        </v-form>
      </div>
    </template>
    <template v-slot:actions>
      <v-btn
        @click="validate"
        :loading="loading"
        :disabled="disabled"
        color="primary"
        elevation="0"
        large
      >
        {{ $t('appointment.reschedule.title') }}
      </v-btn>
    </template>
  </Modal>
</template>

<script>
import moment from 'moment'

import { mapGetters, mapActions, mapMutations } from 'vuex'
import { VMoney } from 'v-money'
import { clearCurrency, checkCurrencyDecimals, toAmount } from '@/util/helpers'
import { required, minLength } from '@/util/errorHandling'

import Modal from '@/components/app/Modal'
import HAmount from '@/components/app/HAmount'
import HSlotSize from '@/components/app/HSlotSize'
import HPeriodPicker from '@/components/lib/HPeriodPicker'
import SelectDate from '@/components/date/SelectDate'
import SelectHour from '@/components/date/SelectHour'

import AppointmentFormDetails from './formFieldsReschedule/AppointmentFormDetails'

import { catalogCurrency, dateHourTimezone } from '@/util/catalog'

export default {
  name: 'AppointmentCancel',

  components: {
    Modal,
    HAmount,
    SelectDate,
    SelectHour,
    HSlotSize,
    HPeriodPicker,
    AppointmentFormDetails,
  },

  directives: {
    money: VMoney
  },

  data() {
    return {
      date: null,
      hour: null,
      loading: false,
      disabled: false,
      displacement: 0,
      displacementComparison: 0,
      slot_size: 0,
      isByHours: true,
      rules: {
        required,
        minLength,
      },
      startDateDelay: null,
      subscriptionType: null,
      userObservations: null,
      userRescheduleReason: null,
      dateOrHourChanged: false,
      message: '',

      errorDisplacementComparison: false,
      catalogCurrency,
    }
  },

  watch: {
    appointment: {
      handler({ date, additionals, type, slot_size }) {
        this.date = moment(date).format('YYYY-MM-DD')
        this.hour = moment(date).format('HH:mm')
        this.slot_size = slot_size ?? 0
        this.isByHours = !slot_size || slot_size === 0
        this.isOutdatedAppointment(this.date, this.hour)

        this.checkDisplacement(additionals)
        this.errorDisplacementComparison = false

        if (type && !this.isAdminOrPartner) {
          var realEstateId = this.appointment?.real_estate?.id
          this.fetchServiceContract({ type, realEstateId })
        }
      },

      deep: true,
    },

    date(date) {
      this.isOutdatedAppointment(moment(date).format('YYYY-MM-DD'), this.hour)
    },

    hour(hour) {
      this.isOutdatedAppointment(moment(this.date).format('YYYY-MM-DD'), hour)
    },

    isByHours(isByHours) {
      this.hour = null
      this.slot_size = isByHours ? 0 : this.slot_size
    },

    allModals() {
      this.dateOrHourChanged = false;
    },
  },

  computed: {
    ...mapGetters([
      'user',
      'appointment',
      'isAppointmentLoading',
      'currentServiceContract',
      'allModals',
    ]),

    country() {
      return this.user?.country ?? localStorage.getItem('user_country')
    },

    currentDisplacement() {
      return toAmount(this.displacementComparison)
    },

    observations: {
      get() {
        this.userObservations = this.appointment?.observations
        return this.appointment?.observations
      },

      set(newObservations) {
        this.userObservations = newObservations
      },
    },

    dateHour: {
      get() {
        return this.appointment?.date
      },

      set() {
        return this.date
      },
    },

    isUrgent() {
      const realEstate = this.user?.real_estate
      const isCustomerWithoutSubscription = ['SINGLE_PURCHASE', 'PREPAID', 'PREPAID_SAAS'].includes(realEstate?.subscription_type)

      let hoursMargin = isCustomerWithoutSubscription ? 24 : 16

      const { urgency_tax, urgency_hours_limit } = this.currentServiceContract || {}

      if (urgency_tax > 0 && urgency_hours_limit > 0) {
          hoursMargin = urgency_hours_limit
      }

      const dateWithMargin = new Date()
      dateWithMargin.setHours(dateWithMargin.getHours() + hoursMargin)

      const date = this.date
            ? moment(this.date).format('YYYY-MM-DD')
            : moment(this.appointment.date).format('YYYY-MM-DD')

      const hour = this.hour
        ? this.hour.replace(' ', '')
        : moment(this.appointment.date).format('HH:mm')

      const rescheduleDate = new Date(dateHourTimezone(date, hour))

      return rescheduleDate < dateWithMargin
    },

    isAdminOrPartner() {
      const roles = localStorage.getItem('access_roles')
      const userRoles = roles?.split(',')
      return userRoles?.includes('ROLE_ADMIN') || userRoles?.includes('ROLE_PARTNER')
    },
  },

  methods: {
    ...mapMutations(['closeAllModals']),
    ...mapActions([
      'fetchUser',
      'fetchBoard',
      'fetchAppointment',
      'rescheduleAppointment',
      'fetchAppointmentAsAdmin',
      'rescheduleAppointmentAsAdmin',
      'fetchServiceContract'
    ]),

    validate() {
      this.errorDisplacementComparison = false

      this.hasErrorOnDisplacement()
      this.isOutdatedAppointment(this.date, this.hour)

      if (!this.isByHours && !this.hour) {
        return
      }

      this.$nextTick(() => {
        if (this.$refs.appointmentFormReschedule.validate()) {
          this.loading = true

          const date = this.date
            ? moment(this.date).format('YYYY-MM-DD')
            : moment(this.appointment.date).format('YYYY-MM-DD')

          const hour = this.hour
            ? this.hour.replace(' ', '').substring(0, 5)
            : moment(this.appointment.date).format('HH:mm')

          const reschedulePayload = {
            id: this.appointment.id,
            date: dateHourTimezone(date, hour),
            observations: this.userObservations,
            reschedule_reason: this.userRescheduleReason || null,
            slot_size: this.slot_size,
            displacement: clearCurrency(this.displacement),
          }

          Promise.resolve(
            this.isAdminOrPartner
              ? this.rescheduleAppointmentAsAdmin(reschedulePayload)
                  .then(() => {
                    if (this.$parent?.refreshMap) this.$parent?.refreshMap()
                    if (this.$parent?.refreshAppointments) this.$parent?.refreshAppointments()
                  })
                  .then(() => this.closeAllModals())
              : this.rescheduleAppointment(reschedulePayload)
                  .then(() => {
                    if (this.$parent?.refreshMap) this.$parent?.refreshMap()
                    if (this.$parent?.refreshAppointments) this.$parent?.refreshAppointments()
                  })
            )
            .then(() => this.$refs?.appointmentFormReschedule?.reset())
            .finally(() => this.loading = false)
        }
      })
    },

    isOutdatedAppointment(date, hour) {
      if (!date) {
        this.disabled = false
        return false
      }

      if (date === 'Invalid date') {
        this.disabled = true
        return true
      }

      if (!hour) {
        // no hour yet — only disable if the date itself (end of day) already passed
        const pastDay = moment().isAfter(moment(date).endOf('day'))
        this.disabled = pastDay
        return pastDay
      }

      if (hour === 'Invalid date') {
        this.disabled = true
        return true
      }

      const start = moment(`${date} ${hour.substring(0, 5)}`)

      if (this.isByHours) {
        if (moment().isAfter(start)) {
          this.disabled = true
          return true
        }
      } else {
        // period mode: valid while end of period hasn't passed
        const end = start.clone().add(this.slot_size || 0, 'minutes')
        if (moment().isAfter(end)) {
          this.disabled = true
          return true
        }
      }

      this.disabled = false
      return false
    },

    checkDisplacement(additionals) {
      if (!additionals || additionals?.length <= 0) {
        this.displacement = 0
        this.displacementComparison = 0
        return
      }

      const displacement = checkCurrencyDecimals(additionals[0].value)

      this.displacement = displacement
      this.displacementComparison = displacement
    },

    hasErrorOnDisplacement() {
      const status = this.appointment.status
      // Comparar as strings do toFixed(2) inverte a ordem ('18.00' < '9.00') e o
      // adicional atual zerado chega como undefined, virando 'NaN': nesse caso
      // qualquer valor novo era barrado.
      const displacement = toAmount(clearCurrency(this.displacement))

      if (status === 'SCHEDULED' && displacement !== 0) {
        if (displacement < this.currentDisplacement) {
          this.errorDisplacementComparison = true
          return
        }
      }

      this.errorDisplacementComparison = false
    },
  },
}
</script>

<style lang="scss" scoped>
.reschedule-type-toggle {
  width: 100%;

  &__item {
    width: 50% !important;

    &.v-item--active {
      color: #fff;
      background-color: #009DD9 !important;
    }
  }

  .v-btn {
    &:nth-child(1) {
      margin-right: -1px;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }

    &:nth-child(2) {
      margin-left: -1px;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }
}
</style>
